import { HttpErrorResponse } from '@angular/common/http';

/**
 * Tipos de error que pueden ocurrir en la aplicación
 */
export enum ErrorType {
  VALIDATION_FAILED = 'VALIDATION_FAILED',
  NETWORK_ERROR = 'NETWORK_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Determina el tipo de error basado en la respuesta HTTP
 * @param error Error HTTP recibido
 * @returns Tipo de error identificado
 */
export function getErrorType(error: HttpErrorResponse): ErrorType {
  // Errores de red (status 0) - ya manejados por network-error.interceptor
  if (error.status === 0) {
    return ErrorType.NETWORK_ERROR;
  }

  // Errores 401 - ya manejados por auth.interceptor
  if (error.status === 401) {
    return ErrorType.UNAUTHORIZED;
  }

  // Error 400 - Validación fallida
  if (error.status === 400) {
    // Verificar si tiene código específico del backend
    const errorCode = error.error?.code || error.error?.Code;
    if (errorCode === 'VALIDATION_FAILED' || errorCode === 'ValidationFailed') {
      return ErrorType.VALIDATION_FAILED;
    }
    // Si tiene errors object, es validación
    if (error.error?.errors && typeof error.error.errors === 'object') {
      return ErrorType.VALIDATION_FAILED;
    }
    return ErrorType.VALIDATION_FAILED;
  }

  // Error 403 - Forbidden
  if (error.status === 403) {
    return ErrorType.FORBIDDEN;
  }

  // Error 404 - Not Found
  if (error.status === 404) {
    return ErrorType.NOT_FOUND;
  }

  // Error 500 - Server Error
  if (error.status === 500) {
    return ErrorType.SERVER_ERROR;
  }

  // Error 503 - Service Unavailable
  if (error.status === 503) {
    return ErrorType.SERVICE_UNAVAILABLE;
  }

  // Error desconocido
  return ErrorType.UNKNOWN;
}

/**
 * Obtiene la clave de traducción para un tipo de error
 * @param errorType Tipo de error
 * @returns Clave de traducción
 */
export function getErrorTranslationKey(errorType: ErrorType): string {
  const translationMap: Record<ErrorType, string> = {
    [ErrorType.VALIDATION_FAILED]: 'common.error.validation',
    [ErrorType.NETWORK_ERROR]: 'common.error.network',
    [ErrorType.SERVER_ERROR]: 'common.error.server',
    [ErrorType.UNAUTHORIZED]: 'common.error.unauthorized',
    [ErrorType.FORBIDDEN]: 'common.error.forbidden',
    [ErrorType.NOT_FOUND]: 'common.error.notFound',
    [ErrorType.SERVICE_UNAVAILABLE]: 'common.error.serviceUnavailable',
    [ErrorType.UNKNOWN]: 'common.error.unknown',
  };

  return translationMap[errorType] || translationMap[ErrorType.UNKNOWN];
}

/**
 * Verifica si un error debe ser manejado por el interceptor
 * Algunos errores ya son manejados por otros interceptores
 * @param error Error HTTP
 * @returns true si debe ser manejado, false si ya fue manejado
 */
export function shouldHandleError(error: HttpErrorResponse): boolean {
  // Errores de red (status 0) - ya manejados por network-error.interceptor
  if (error.status === 0) {
    return false;
  }

  // Errores 401 - ya manejados por auth.interceptor
  if (error.status === 401) {
    return false;
  }

  return true;
}

